import React from 'react';
import { Content } from '../jet-ui/Content/Content';
import { Breadcrumb } from '../jet-ui/Breadcrumb/Breadcrumb';
import { Card } from '../jet-ui/Card/Card';
import {
  useDisplayPreferences,
  MONITOR_COLUMNS,
  TIMEZONE_OPTIONS,
  ROWS_PER_PAGE_OPTIONS,
} from '../common/hooks/useDisplayPreferences';

import './DisplayPreferences.css';

/**
 * A page for configuring user display preferences including timezone
 * and visible columns on the Monitor Recent table.
 */
export const DisplayPreferences: React.FC = () => {
  const { prefs, savePreferences } = useDisplayPreferences();
  const [timezone, setTimezone] = React.useState(prefs.timezone);
  const [columns, setColumns] = React.useState<string[]>(prefs.monitorColumns);
  const [flowsRowsPerPage, setFlowsRowsPerPage] = React.useState(prefs.flowsRowsPerPage);
  const [monitorRowsPerPage, setMonitorRowsPerPage] = React.useState(prefs.monitorRowsPerPage);
  const [saved, setSaved] = React.useState(false);

  // Sync local state when prefs load
  React.useEffect(() => {
    setTimezone(prefs.timezone);
    setColumns(prefs.monitorColumns);
    setFlowsRowsPerPage(prefs.flowsRowsPerPage);
    setMonitorRowsPerPage(prefs.monitorRowsPerPage);
  }, [prefs.timezone, prefs.monitorColumns, prefs.flowsRowsPerPage, prefs.monitorRowsPerPage]);

  const handleToggleColumn = (key: string, required: boolean) => {
    if (required) return;
    setColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
    setSaved(false);
  };

  const handleSave = () => {
    savePreferences({ timezone, monitorColumns: columns, flowsRowsPerPage, monitorRowsPerPage });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <Content>
      <div>
        <Breadcrumb href="/">Home</Breadcrumb>
        <Breadcrumb href="/display">Display</Breadcrumb>
      </div>
      <h1>Display Preferences</h1>
      <Card>
        <div className="DisplayPreferences__Section">
          <h2>Timezone</h2>
          <p>Select the timezone for displayed dates and times.</p>
          <select
            className="DisplayPreferences__TimezoneSelect"
            value={timezone}
            onChange={e => { setTimezone(e.target.value); setSaved(false); }}
          >
            {TIMEZONE_OPTIONS.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        <div className="DisplayPreferences__Section">
          <h2>Monitor Columns</h2>
          <p>Configure which columns are visible on the Monitor Recent table.</p>
          <ul className="DisplayPreferences__ColumnList">
            {MONITOR_COLUMNS.map(col => {
              const isEnabled = columns.includes(col.key);
              const itemClass = col.required
                ? 'DisplayPreferences__ColumnItem DisplayPreferences__ColumnItem--disabled'
                : 'DisplayPreferences__ColumnItem';
              return (
                <li
                  key={col.key}
                  className={itemClass}
                  onClick={() => handleToggleColumn(col.key, col.required)}
                >
                  <i className="material-icons">
                    {isEnabled || col.required ? 'radio_button_checked' : 'radio_button_unchecked'}
                  </i>
                  {col.label}
                  {col.required && <span style={{ fontSize: 12, color: '#999', marginLeft: 4 }}>(required)</span>}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="DisplayPreferences__Section">
          <h2>Rows Per Page - Flows</h2>
          <p>Number of rows to display per page on the Flows table.</p>
          <div className="DisplayPreferences__RowsPerPage">
            {ROWS_PER_PAGE_OPTIONS.map(n => (
              <span
                key={n}
                className={`DisplayPreferences__RowsPerPageOption${flowsRowsPerPage === n ? ' DisplayPreferences__RowsPerPageOption--selected' : ''}`}
                onClick={() => { setFlowsRowsPerPage(n); setSaved(false); }}
              >
                {n}
              </span>
            ))}
          </div>
        </div>

        <div className="DisplayPreferences__Section">
          <h2>Rows Per Page - Monitor</h2>
          <p>Number of rows to display per page on the Monitor Recent table.</p>
          <div className="DisplayPreferences__RowsPerPage">
            {ROWS_PER_PAGE_OPTIONS.map(n => (
              <span
                key={n}
                className={`DisplayPreferences__RowsPerPageOption${monitorRowsPerPage === n ? ' DisplayPreferences__RowsPerPageOption--selected' : ''}`}
                onClick={() => { setMonitorRowsPerPage(n); setSaved(false); }}
              >
                {n}
              </span>
            ))}
          </div>
        </div>

        <div>
          <button className="DisplayPreferences__SaveButton" onClick={handleSave}>
            Save
          </button>
          {saved && <span className="DisplayPreferences__SavedMessage">Saved!</span>}
        </div>
      </Card>
    </Content>
  );
};
